import type {
  ReviewCaseState,
  ReviewEvent,
  ReviewExtract,
  ReviewGridBody,
  ReviewOperation,
  ReviewValidation,
} from '../schemas/session-review.js';

/**
 * The pure rules of the REVIEW (relecture.md §5, §6, issue #187), run by BOTH
 * sides for the reason `protocolProblems` gives: the server refuses and derives,
 * the page draws the same thing while the operator works. No DOM, no database,
 * no clock - what a rule needs is passed in.
 */

/** What an overlap reads of an extract: where it sits. */
type Placed = Pick<ReviewExtract, 'turn' | 'start' | 'end'>;

/**
 * Do two extracts overlap (§5.2)? Same turn, and their ranges cross - `end` is
 * exclusive, so two extracts that merely touch (`a.end === b.start`) do not.
 */
export function extractsOverlap(a: Placed, b: Placed): boolean {
  return a.turn === b.turn && a.start < b.end && b.start < a.end;
}

/**
 * The extracts of OTHER cases that overlap this one - the « aussi en 6 » of the
 * popup and of the locked « ? » (§5.3, §5.4). A same-case overlap is not a
 * cross-placement: the passage is already in that case.
 */
export function crossPlacements(
  extract: Placed & Pick<ReviewExtract, 'objectiveId'>,
  all: readonly ReviewExtract[],
): ReviewExtract[] {
  return all.filter(
    (other) => other.objectiveId !== extract.objectiveId && extractsOverlap(extract, other),
  );
}

/** What the setting is read from: the grid frozen on the review. */
type GridSetting = Pick<ReviewGridBody, 'oneObjectivePerPassage'>;

/**
 * Is the « ? » of this extract LOCKED (§5.4, D28)? Under the setting « un
 * passage, un seul objectif », a passage that sits in two cases carries its
 * « ? » on both placements and neither can lift it - until one of the two is
 * removed. Unticked, nothing is ever locked.
 */
export function isDoubtLocked(
  extract: Placed & Pick<ReviewExtract, 'objectiveId'>,
  all: readonly ReviewExtract[],
  grid: GridSetting,
): boolean {
  return grid.oneObjectivePerPassage && crossPlacements(extract, all).length > 0;
}

/** What a `range` puts in doubt as a consequence of itself (D28). */
export type RangeImplied = {
  /** The new extract is born with its « ? » up. */
  readonly doubtNew: boolean;
  /** The ids of the EXISTING extracts of other cases it overlaps, whose « ? » is not up yet. */
  readonly doubtIds: readonly string[];
};

/**
 * What a `range` implies (§5.4, D28). Under the setting, ranging a passage that
 * overlaps an extract of ANOTHER case puts a « ? » on both placements: the new
 * extract is born in doubt, and each overlapped one is returned to be put in
 * doubt. Unticked, or with no cross-overlap, it implies nothing.
 *
 * `doubtIds` leaves out an extract already in doubt, so the writer journals no
 * line for a gesture that changes nothing. The new extract has no id yet, which
 * is why the answer is a flag and not a list that would include it.
 */
export function rangeImplies(
  op: Placed & Pick<Extract<ReviewOperation, { op: 'range' }>, 'objectiveId'>,
  all: readonly ReviewExtract[],
  grid: GridSetting,
): RangeImplied {
  if (!grid.oneObjectivePerPassage) return { doubtNew: false, doubtIds: [] };
  const crossed = crossPlacements(op, all);
  if (crossed.length === 0) return { doubtNew: false, doubtIds: [] };
  return {
    doubtNew: true,
    doubtIds: crossed.filter((other) => !other.doubt).map((other) => other.id),
  };
}

/* ------------------------------------------------------------- validations */

/** What the journal is read for: the instant, the operation and the extract rows. */
type JournalLine = Pick<ReviewEvent, 'at' | 'op' | 'before' | 'after'>;

/** The operations that CHANGE a case (D12): a note, a validation or a noyau does not. */
const CASE_CHANGING_OPERATIONS: ReadonlySet<ReviewEvent['op']> = new Set([
  'range',
  'unrange',
  'set-doubt',
]);

/** The case an extract row of the journal belongs to, or null when the line carries none. */
function objectiveOfRow(row: unknown): string | null {
  if (typeof row !== 'object' || row === null || !('objectiveId' in row)) return null;
  return typeof row.objectiveId === 'string' ? row.objectiveId : null;
}

/** A stamp as a number, or NaN when it does not read. */
function instant(at: string): number {
  return Date.parse(at);
}

/**
 * The instant of the last MODIFICATION of a case (§6, D12): the latest `range`,
 * `unrange` or `set-doubt` that touches it, read off the `ReviewExtract` rows
 * the journal keeps in `before` and `after`. Null when none did.
 *
 * A note on an extract or on the case, a validation, an annotation or the noyau
 * never count: « une note seule ne périme rien ». A move between cases is a
 * `range` and an `unrange`, so it modifies both.
 */
export function caseChangedAt(objectiveId: string, events: readonly JournalLine[]): string | null {
  let latest: string | null = null;
  for (const event of events) {
    if (!CASE_CHANGING_OPERATIONS.has(event.op)) continue;
    if (objectiveOfRow(event.before) !== objectiveId && objectiveOfRow(event.after) !== objectiveId)
      continue;
    if (latest === null || instant(event.at) > instant(latest)) latest = event.at;
  }
  return latest;
}

/**
 * Is a validation IN FORCE (§6)? It is when it is not older than the last
 * modification of its case; a case never modified keeps every validation in
 * force. The stamp is compared as an instant, and an unreadable one is NOT in
 * force: the cautious reading of a validation nobody can date.
 *
 * `>=` and not `>`: a gesture batch stamps its operations with one instant, and
 * a `range` followed by a `validate-case` in the same request must leave the
 * validation standing.
 */
export function currentValidation(
  validation: Pick<ReviewValidation, 'at'>,
  changedAt: string | null,
): boolean {
  if (changedAt === null) return true;
  return instant(validation.at) >= instant(changedAt);
}

/**
 * The state of a case, derived (§6) and never stored:
 *
 * - `valid` / `invalid` - the MOST RECENT validation is in force, and says so -
 *   an EMPTY case included: « this case stays empty » is a judgement a colleague
 *   validates like any other (the mockup shows « validée » on an empty case);
 * - `to-revalidate` - the case has been validated, but every validation predates
 *   its last modification;
 * - `empty` - no extract and no validation;
 * - `to-validate` - an extract and no validation yet.
 *
 * Several validations in force are read by the latest one (a colleague may
 * disagree with a first verdict); on a tie, the later in the list wins, the
 * list being in the order of writing.
 */
export function caseStateOf(
  objectiveId: string,
  extracts: readonly Pick<ReviewExtract, 'objectiveId'>[],
  validations: readonly Pick<ReviewValidation, 'objectiveId' | 'verdict' | 'at'>[],
  changedAt: string | null,
): ReviewCaseState {
  const own = validations.filter((validation) => validation.objectiveId === objectiveId);
  if (own.length === 0) {
    return extracts.some((extract) => extract.objectiveId === objectiveId)
      ? 'to-validate'
      : 'empty';
  }
  const latest = own.reduce((newest, validation) =>
    instant(validation.at) >= instant(newest.at) ? validation : newest,
  );
  return currentValidation(latest, changedAt) ? latest.verdict : 'to-revalidate';
}
